import { CSmartTable } from '@coreui/react-pro'
import { Checkbox, Chip, FormControlLabel, Pagination, Typography, useTheme } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { useEffect, useState } from 'react'

dayjs.extend(utc)
dayjs.extend(timezone)

function guessTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

function safeDayjsInTz(zone) {
  try {
    const z = zone || 'UTC'
    const d = dayjs().tz(z)
    return d.isValid() ? d : dayjs()
  } catch {
    return dayjs()
  }
}
import { EDVCParent } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import {
  PayoutReportsToolbar,
  PayoutReportsDateRangeBox,
} from 'src/clinic/views/clinicBrandPayoutReports/styledComponents'
import { getCPDataWithParams } from 'src/clinic/services'
import { GenericHeaderBackButton } from 'src/consumer/components/ActivityPlansPage/MyPlanComponents'
import {
  BOOKED_EVENT_MODES,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPES_ARRAY,
  MOMENT_FORMATS,
  PAYMENT_METHOD_KV_MAP,
} from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import {
  getCSmartTableColumnData,
  getGenderFromGenderId,
  isValidNumber,
} from 'src/consumer/helpers/utilFunctions'
import { PageMainContentContainer } from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'
import {
  getDataMapFromCommonResponse,
  getPriceStringWithCurrency,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import BookedEventLogsDrawer from 'src/clinic/components/ClinicManageBookingsPage/components/BookedEventLogsDrawer'
import ClinicBrandPayoutReportsViewDetails from './components/ClinicBrandPayoutReportsViewDetails'
import PayoutDistributionReportFeeDetails from './components/PayoutDistributionReportFeeDetails'
import PayoutDistributionReportModelDetails from './components/PayoutDistributionReportModelDetails'
import PayoutReportSummary from './components/PayoutReportSummary'
import {
  BrandPayoutStyledSmartTable,
  PayoutReportExportPaper,
  PayoutReportsPaginationBar,
} from 'src/clinic/views/clinicBrandPayoutReports/styledComponents'
import { buildClinicPayoutReportsDoctor, EXPORT_TYPES, getSettlementDetails } from './util'
import ExportPayoutReports from './components/ExportPayoutReports'
import ExportTypeSelector from 'src/clinic/views/clinicBrandPayoutReports/components/ExportTypeSelector'
import ExportPayoutReportsDialogWithButton from 'src/common/components/payoutReports/ExportPayoutReportsDialogWithButton'
import { PaymentConfigMenuWithDialog } from 'src/supportPortal/components/common/paymentConfig/PaymentConfigExample'

export default function ClinicBrandPayoutReports({
  apiUrl,
  summaryApiUrl = '/api/secure/clinic-admin/reports/payout-reports/summary',
  hideBrandName,
  isForClinicBrand = false, // Default value
  isForServiceProvider = false,
  showSummaryReport = false,
  thisClinicBrandData,
  clinicBrandId,
  hideBackButton = false,
  eventType,
  allEventTypes,
  showAdminActions = false,
  controlledReportDate,
  controlledReportEndDate,
  onDateRangeChange,
  hideExportAndPaymentInToolbar = false,
}) {
  const theme = useTheme()
  const [internalReportDate, setInternalReportDate] = useState(() =>
    safeDayjsInTz(guessTimezone()).subtract(1, 'month').startOf('month'),
  )
  const [internalReportEndDate, setInternalReportEndDate] = useState(() =>
    safeDayjsInTz(guessTimezone()).subtract(1, 'month').endOf('month'),
  )

  const reportDate = controlledReportDate ?? internalReportDate
  const reportEndDate = controlledReportEndDate ?? internalReportEndDate

  function setReportDate(d) {
    if (controlledReportDate == null) setInternalReportDate(d)
  }
  function setReportEndDate(d) {
    if (controlledReportEndDate == null) setInternalReportEndDate(d)
  }
  const [listData, setListData] = useState([])
  const [paginationData, setPaginationData] = useState({ page: 0, totalPages: 0 })
  const [loadingPageData, setLoadingPageData] = useState(false)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [summaryReport, setSummaryReport] = useState()
  const [excelDialogOpen, setExcelDialogOpen] = useState(false)
  const [exportType, setExportType] = useState(EXPORT_TYPES.excel.id)
  const [selectedBookedEventForLogs, setSelectedBookedEventForLogs] = useState(null)
  const [showBookedEventLogsDrawer, setShowBookedEventLogsDrawer] = useState(false)
  const [includeNonAumhumAffiliations, setIncludeNonAumhumAffiliations] = useState(false)

  function openBookedEventLogsDialog(bookedEvent) {
    setSelectedBookedEventForLogs(bookedEvent)
    setShowBookedEventLogsDrawer(true)
  }

  function closeBookedEventLogsDialog() {
    setShowBookedEventLogsDrawer(false)
    setSelectedBookedEventForLogs(null)
  }

  async function getReports({
    page = 0,
    reportDateMoment = safeDayjsInTz(guessTimezone()),
    reportEndDateMoment = safeDayjsInTz(guessTimezone()),
  }) {
    setLoadingPageData(true)
    const resp = await getCPDataWithParams(apiUrl, {
      page,
      clinicBrandId: clinicBrandId,
      eventType,
      startDate: reportDateMoment.startOf('day').format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment
        
        .endOf('day')
        .add(1, 'days')
        .format(MOMENT_FORMATS.javaLocalDateFormat),
    })
    if (resp.status === 200) {
      const data = resp.data.content
      const paginationData = resp?.data?.pageable

      const pageNumber = paginationData?.pageNumber
      const totalPages = resp?.data?.totalPages

      setListData(data)
      setPaginationData((prev) => ({ ...prev, page: pageNumber, totalPages: totalPages }))
    }
    setLoadingPageData(false)
  }

  async function getSummaryReports({
    reportDateMoment,
    reportEndDateMoment,
    includeNonAumhumOverride,
  }) {
    setLoadingSummary(true)
    const includeNonAumhum =
      includeNonAumhumOverride !== undefined ? includeNonAumhumOverride : includeNonAumhumAffiliations

    const resp = await getCPDataWithParams(summaryApiUrl, {
      clinicBrandId: clinicBrandId,
      eventType,
      startDate: reportDateMoment.startOf('day').format(MOMENT_FORMATS.javaLocalDateFormat),
      endDate: reportEndDateMoment
        
        .endOf('day')
        .add(1, 'days')
        .format(MOMENT_FORMATS.javaLocalDateFormat),
      includeNonAumhumAffiliations: includeNonAumhum,
    })
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        const reportData = data?.['payout-distribution-summary-for-clinic']
        setSummaryReport(reportData)
      }
    }
    setLoadingSummary(false)
  }

  async function setup() {
    getDataWithSortPageOrSize(0, undefined, undefined, undefined)
  }

  function onPrevClick() {
    getDataWithSortPageOrSize(
      undefined,
      undefined,
      undefined,
      reportDate.subtract(1, 'days'),
    )
  }

  function onNextClick() {
    getDataWithSortPageOrSize(undefined, undefined, undefined, reportDate.add(1, 'days'))
  }

  function getAndSetAppropriatePaginationData(page, sort, pageSize) {
    let pg = page
    let srt = sort
    let pgSize = pageSize
    if (!isValidNumber(pg)) {
      pg = paginationData.page
    }

    setPaginationData((prev) => ({
      ...prev,
      page: pg,
      pageSize: pgSize,
    }))

    return { page: pg, sort: srt, pageSize: pgSize }
  }

  function getAndSetAppropriateDate(newMoment) {
    let date = newMoment
    if (!date) {
      if (!reportDate) {
        date = dayjs()
      } else {
        date = reportDate
      }
    }
    setReportDate(date)
    return date
  }

  function getAndSetAppropriateEndDate(newMoment) {
    let date = newMoment
    if (!date) {
      if (!reportEndDate) {
        date = dayjs()
      } else {
        date = reportEndDate
      }
    }
    setReportEndDate(date)
    return date
  }

  async function getDataWithSortPageOrSize(
    page,
    sort,
    pageSize,
    newSelectedMoment,
    newSelectedEndMoment,
    getSummary = true,
    includeNonAumhumOverride,
  ) {
    setLoadingPageData(true)

    const paginationData = getAndSetAppropriatePaginationData(page, sort, pageSize)
    const newMoment = getAndSetAppropriateDate(newSelectedMoment)
    const newEndMoment = getAndSetAppropriateEndDate(newSelectedEndMoment)
    if (onDateRangeChange && (newSelectedMoment != null || newSelectedEndMoment != null)) {
      onDateRangeChange(newMoment, newEndMoment)
    }
    let promises = [
      getReports({
        page: paginationData?.page,
        reportDateMoment: newMoment,
        reportEndDateMoment: newEndMoment,
      }),
    ]

    if (showSummaryReport && getSummary) {
      promises = [
        ...promises,
        getSummaryReports({
          reportDateMoment: newMoment,
          reportEndDateMoment: newEndMoment,
          includeNonAumhumOverride,
        }),
      ]
    }
    await Promise.all(promises)

    setLoadingPageData(false)
  }

  function handleSelectExportType(type) {
    setExportType(type)
    setExcelDialogOpen(true)
  }

  useEffect(() => {
    setup()
  }, [eventType])

  const handleDateRangeChange = (start, end) => {
    if (start && end) {
      getDataWithSortPageOrSize(undefined, undefined, undefined, start, end)
    }
  }

  return (
    <>
      {!hideBackButton && (
        <GenericHeaderBackButton
          clickfunction={() => window.history.back()}
          styles={{ margin: '20px' }}
        />
      )}
      <PageMainContentContainer style={{ maxWidth: '1600px', width: '100%' }}>
        <EDVCParent>
          <div className="d-flex flex-column align-items-stretch gap-1 w-100" style={{ maxWidth: '100%' }}>
            <PayoutReportsToolbar className="w-100">
              <div className="w-100 d-flex flex-column" style={{ position: 'relative' }}>
                <div className="d-flex flex-wrap align-items-center justify-content-between w-100 gap-2">
                  <PayoutReportsDateRangeBox>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="From date"
                        value={reportDate}
                        onChange={(newVal) => {
                          if (newVal) {
                            const end =
                              reportEndDate.isBefore(newVal, 'day') || reportEndDate.isSame(newVal, 'day')
                                ? newVal.endOf('day')
                                : reportEndDate
                            handleDateRangeChange(newVal, end)
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { minWidth: 160 },
                          },
                        }}
                      />
                      <DatePicker
                        label="To date"
                        value={reportEndDate}
                        minDate={reportDate}
                        onChange={(newVal) => {
                          if (newVal) handleDateRangeChange(reportDate, newVal)
                        }}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { minWidth: 160 },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </PayoutReportsDateRangeBox>
                  {!hideExportAndPaymentInToolbar && (
                    <div style={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' }}>
                      <ExportPayoutReportsDialogWithButton
                        dataFetchingApiUrl={apiUrl}
                        startDate={reportDate}
                        endDate={reportEndDate}
                        entityId={clinicBrandId}
                        entityType={'clinic-brand'}
                        eventType={eventType}
                        allEventTypes={allEventTypes}
                        fileNamePrefix={
                          eventType === EVENT_TYPE_CONSULTATION
                            ? 'Consultation_Payout_Reports'
                            : 'Therapy_Payout_Reports'
                        }
                        extraParams={{ clinicBrandId }}
                        payoutReportDataArrayToExcelFormatArrayBuilder={
                          buildClinicPayoutReportsDoctor
                        }
                      />
                      {showAdminActions && (
                        <PaymentConfigMenuWithDialog
                          payoutEntityId={clinicBrandId}
                          payoutEntityType={'clinic-brand'}
                          entityName={'Clinic Brand'}
                        />
                      )}
                    </div>
                  )}
                </div>

                {showSummaryReport && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(2),
                        flexWrap: 'wrap',
                        marginBottom: theme.spacing(1),
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={includeNonAumhumAffiliations}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setIncludeNonAumhumAffiliations(checked)
                              getDataWithSortPageOrSize(
                                paginationData?.page,
                                undefined,
                                undefined,
                                reportDate,
                                reportEndDate,
                                true,
                                checked,
                              )
                            }}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            Include non-AUMHUM affiliated sessions in summary
                          </Typography>
                        }
                      />
                    </div>
                    <PayoutReportSummary
                      summaryReport={summaryReport}
                      eventType={eventType}
                      allEventTypes={allEventTypes}
                      currentDate={reportDate}
                      previousDate={reportEndDate}
                      filters={{ clinicBrandId: clinicBrandId, eventType }}
                      loadingSummary={loadingSummary}
                      totalSessionTitle={`Total ${
                        EVENT_TYPES_ARRAY.find((eve) => eve?.id === eventType)?.title || ''
                      } Sessions`}
                      includeNonAumhumInSummary={includeNonAumhumAffiliations}
                    />
                  </>
                )}
              </div>
            </PayoutReportsToolbar>
            {paginationData?.totalPages > 1 && (
              <PayoutReportsPaginationBar className="w-100" sx={{ py: 0 }}>
                <Typography className="pagination-label" component="span">
                  Page {paginationData?.page + 1} of {paginationData?.totalPages}
                </Typography>
                <Pagination
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                  size="medium"
                  count={paginationData?.totalPages}
                  page={(paginationData?.page ?? 0) + 1}
                  onChange={(_, page) =>
                    getDataWithSortPageOrSize(
                      page - 1,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      false,
                    )
                  }
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </PayoutReportsPaginationBar>
            )}
            <BrandPayoutStyledSmartTable sx={{ mt: 0 }}>
              <CSmartTable
                key={`payout-table-${reportDate?.valueOf?.() ?? 0}-${reportEndDate?.valueOf?.() ?? 0}-${listData?.length ?? 0}-${paginationData?.page ?? 0}`}
                items={listData}
                columns={columns({
                  isForBrand: isForClinicBrand,
                  isForServiceProvider: isForServiceProvider,
                })}
                scopedColumns={scopedColumns({
                  isForBrand: isForClinicBrand,
                  showAdminActions,
                  refreshReports: () => {
                    getDataWithSortPageOrSize(
                      paginationData?.page,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      true,
                    )
                  },
                  showBookingLogsButton: true,
                  openBookedEventLogsDialog,
                  theme,
                })}
                loading={loadingPageData}
                tableProps={{
                  hover: true,
                  striped: true,
                  responsive: true,
                }}
                // columnSorter
                // pagination
                // onColumnFilterChange={(filter) => {
                //   getDataWithSortPageOrSize(1)
                //   setColumnFilter(filter)
                // }}
                // onItemsPerPageChange={(pageSize) => {
                //   getDataWithSortPageOrSize(1, undefined, pageSize)
                //   setItemsPerPage(pageSize)
                // }}
                itemsPerPage={10000000000000000}
              />
            </BrandPayoutStyledSmartTable>
          </div>
        </EDVCParent>
        {selectedBookedEventForLogs && (
          <BookedEventLogsDrawer
            open={showBookedEventLogsDrawer}
            onClose={closeBookedEventLogsDialog}
            bookedSlot={{
              ...selectedBookedEventForLogs,
              id: selectedBookedEventForLogs?.bookedEventId,
              startTime: selectedBookedEventForLogs?.bookedEventStartTime,
              endTime: selectedBookedEventForLogs?.bookedEventEndTime,
              mentorName: selectedBookedEventForLogs?.mentorName,
              patientInfo: {
                name: selectedBookedEventForLogs?.patientName,
                patientNumber: selectedBookedEventForLogs?.patientNumber,
                gender: selectedBookedEventForLogs?.patientGender,
                dateOfBirth: selectedBookedEventForLogs?.patientDateOfBirth,
                mobile: selectedBookedEventForLogs?.patientMobile,
              },
            }}
            apiEndpoint="/api/secure/cc/support/events/logs/get"
          />
        )}
      </PageMainContentContainer>
      {/* <Dialog PaperComponent={PayoutReportExportPaper} open={excelDialogOpen}>
        <ExportPayoutReports
          exportType={exportType}
          closeDialog={() => setExcelDialogOpen(false)}
          dataFetchingApiUrl={apiUrl}
          startDate={reportDate}
          endDate={reportEndDate}
          entityId={clinicBrandId}
          entityType={'clinic-brand'}
          eventType={eventType}
          allEventTypes={allEventTypes}
          fileNamePrefix={
            eventType === EVENT_TYPE_CONSULTATION
              ? 'Consultation_Payout_Reports'
              : 'Therapy_Payout_Reports'
          }
          extraParams={{ clinicBrandId }}
          payoutReportDataArrayToExcelFormatArrayBuilder={buildClinicPayoutReportsDoctor}
        />
      </Dialog> */}
    </>
  )
}

const columns = ({ isForBrand }) => [
    getCSmartTableColumnData({
      key: 'patientName',
      label: 'Patient Name',
      filter: false,
      sorter: false,
      _style: { maxWidth: '200px' },
    }),
    getCSmartTableColumnData({
      key: 'eventDetails',
      label: 'Booking Details',
      filter: false,
      sorter: false,
      _style: { maxWidth: '200px' },
    }),
    getCSmartTableColumnData({
      key: 'affiliation',
      label: 'Affiliation',
      filter: false,
      sorter: false,
      _style: { minWidth: '180px' },
    }),
    // getCSmartTableColumnData({
    //   // key: 'sessionNumOfSameTypeWithSameMentor',
    //   key: 'visitDetails',
    //   label: 'Visit Details',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '200px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'clinicBrandDetails',
    //   label: 'Clinic Brand Details',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '200px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'clinicLocationName',
    //   label: 'Clinic Location',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '200px' },
    // }),
    // ...(!!isForBrand
    //   ? []
    //   : [
    //       getCSmartTableColumnData({
    //         // key: 'mentorName',
    //         key: 'mentorDetails',
    //         label: 'Service Provider Details',
    //         filter: false,
    //         sorter: false,
    //         _style: { maxWidth: '200px' },
    //       }),
    //     ]),
    // // getCSmartTableColumnData({
    // //   key: 'clinicLocation',
    // //   label: 'Clinic Location',
    // //   filter: false,
    // //   sorter: false,
    // //   _style: { maxWidth: '200px' },
    // // }),

    getCSmartTableColumnData({
      key: 'feeDetails',
      label: 'Fee Details',
      filter: false,
      sorter: false,
      _style: { minWidth: '240px' },
    }),
    // getCSmartTableColumnData({
    //   key: 'collectedBy',
    //   label: 'Collected Details',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '200px' },
    // }),
    getCSmartTableColumnData({
      key: 'settlementDetails',
      label: 'Transaction Needed',
      filter: false,
      sorter: false,
      _style: { maxWidth: '100px' },
    }),
    // getCSmartTableColumnData({
    //   key: 'paymentMethod',
    //   label: 'Payment Method',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '200px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'sourceSharePercentage',
    //   label: 'Clinic Brand Share (%)',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '150px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'sourceShare',
    //   label: 'Clinic Brand Share Amount',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '200px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'mentorSharePercentage',
    //   label: 'Mentor Share (%)',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '150px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'mentorShare',
    //   label: 'Mentor Share Amount',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '200px' },
    // }),

    // getCSmartTableColumnData({
    //   key: 'grossMargin',
    //   label: 'Gross Margin',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '150px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'modelIds',
    //   label: 'Model IDs',
    //   filter: false,
    //   sorter: false,
    //   _style: { maxWidth: '250px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'startTime',
    //   label: 'Event Start Time',
    //   filter: false,
    //   sorter: true,
    //   _style: { maxWidth: '200px' },
    // }),
    // getCSmartTableColumnData({
    //   key: 'transactionNeeded',
    //   label: 'Transaction Needed',
    //   filter: false,
    //   sorter: true,
    //   _style: { maxWidth: '200px' },
    // }),
    getCSmartTableColumnData({
      key: 'view',
      label: 'Actions',
      filter: false,
      sorter: true,
      _style: { minWidth: '280px', width: '280px' },
    }),
  ]

const scopedColumns = ({
  isForBrand,
  propEventType,
  showAdminActions,
  refreshReports,
  showBookingLogsButton = false,
  openBookedEventLogsDialog,
  theme,
}) => {
  return {
    collectedBy: (item) => (
      <td>
        <div className="d-flex flex-column">
          <span>
            (
            {item?.paymentMethod ===
            PAYMENT_METHOD_KV_MAP.BOOKING_FEE_PAYMENT_METHOD_COLLECTED_BY_AUMHUM
              ? 'Collected By AUMHUM'
              : 'Collected By Clinic'}
            )
          </span>
          <span>{item?.collectedBy}</span>
          <span>
            {item?.collectedAt
              ? dayjs
                  .utc(item?.collectedAt)
                  .local()
                  .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
              : 'No Data'}
          </span>
        </div>
      </td>
    ),
    clinicBrandDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportModelDetails
            report={item}
            getSharePercent={(item) => item?.sourceSharePercentage}
            getShareAmount={(item) => item?.sourceShare}
            getEntityTitle={(item) => item?.sourceName}
            getEntityFeeModelDetails={(item) => item?.sourceEntityFeeModelUsed}
            getFeeModelDetails={(item) => item?.sourceFeeModelUsed}
            getCurrencyCode={(item) => item?.currency}
          />
        </td>
      )
    },
    grossMargin: (item) => (
      <td>
        {isValidNumber(item?.grossMargin)
          ? getPriceStringWithCurrency(item?.grossMargin, item?.currency)
          : 'Cannot Calculate'}
      </td>
    ),
    visitDetails: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <span>Visit Number: {item?.sessionNumOfSameTypeWithSameMentor}</span>
            <span>
              Fee Paid:{' '}
              {isValidNumber(item?.feeCollected)
                ? getPriceStringWithCurrency(item?.feeCollected, item?.currency)
                : 'No Data'}
            </span>
          </div>
        </td>
      )
    },
    eventDetails: (item) => {
      const foundEventType = EVENT_TYPES_ARRAY.find((type) => item?.eventType === type?.id)
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <div>
              {foundEventType.mentorNoun || 'Service Provider'}: {item?.mentorName}
            </div>
            <div>
              <div>
                <span>
                  Scheduled Start Time<strong>: </strong>
                  {item?.bookedEventStartTime
                    ? dayjs
                        .utc(item?.bookedEventStartTime)
                        .local()
                        .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)
                    : 'No Data'}
                </span>
              </div>
              {BOOKED_EVENT_MODES?.[item?.mode]?.altTitle ? (
                <Chip label={BOOKED_EVENT_MODES?.[item?.mode]?.altTitle} />
              ) : (
                ''
              )}
              {item?.testPatient ? <Chip color="warning" label="Test Booking" /> : ''}
            </div>
            <div>Visit #: {item?.sessionNumOfSameTypeWithSameMentor}</div>
            {/* <div className="flex-shrink-0">
              {item?.markedCompleted ? (
                <Chip color="success" size="small" label="Session completed" />
              ) : (
                <Chip color="default" size="small" label="Session not completed" />
              )}
            </div> */}
          </div>
        </td>
      )
    },
    mentorDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportModelDetails
            report={item}
            getEntityTitle={(item) => item?.mentorName}
            getSharePercent={(item) => item?.mentorSharePercentage}
            getShareAmount={(item) => item?.mentorShare}
            getEntityFeeModelDetails={(item) => item?.mentorEntityFeeModelUsed}
            getFeeModelDetails={(item) => item?.mentorFeeModelUsed}
            getCurrencyCode={(item) => item?.currency}
          />
        </td>
      )
    },
    feeDetails: (item) => {
      return (
        <td>
          <PayoutDistributionReportFeeDetails
            report={item}
            getClinicShare={(item) => item?.sourceShare}
            getClinicSharePercentage={(item) => item?.sourceSharePercentage}
            getCurrency={(item) => item?.currency}
            getMinPayableValue={(item) => item?.minFeeUsedForCalculations}
          />
        </td>
      )
    },
    startTime: (item) => {
      return (
        <td>
          {dayjs
            .utc(item?.bookedEventStartTime)
            .local()
            .format(MOMENT_FORMATS.completeTimeDetailsWithMonthShortForm)}
        </td>
      )
    },
    patientName: (item) => {
      const genderLabel = getGenderFromGenderId(item?.patientGender)?.label || 'NA'
      const ageStr = getAgeFromDobWithTime(item?.patientDateOfBirth)
      return (
        <td>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(0.75),
              paddingTop: theme.spacing(0.5),
              paddingBottom: theme.spacing(0.5),
            }}
          >
            {item?.patientNumber && (
              <Chip
                label={item.patientNumber}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  minHeight: 26,
                  '& .MuiChip-label': { px: 1.25, py: 0.5 },
                }}
              />
            )}
            {item?.patientName && (
              <span style={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 }}>
                {item.patientName}
              </span>
            )}
            <span
              style={{
                fontSize: '0.8125rem',
                color: theme.palette.text.secondary,
                lineHeight: 1.4,
              }}
            >
              {genderLabel} · {ageStr}
            </span>
          </div>
        </td>
      )
    },
    settlementDetails: (item) => {
      const settleMentString = getSettlementDetails(item)
      return (
        <td>
          <Chip color={settleMentString.color} label={settleMentString.string} />
        </td>
      )
    },
    affiliation: (item) => {
      const isAffiliatedToAumhum = item?.affiliatedToAUMHUM !== false
      return (
        <td>
          {isAffiliatedToAumhum ? (
            <Chip
              size="small"
              label="AUMHUM"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          ) : (
            <Chip
              size="small"
              label="Not affiliated to AUMHUM"
              color="warning"
              sx={{ fontWeight: 600 }}
            />
          )}
        </td>
      )
    },
    view: (item) => {
      return (
        <td>
          <ClinicBrandPayoutReportsViewDetails
            report={item}
            showAdminActions={showAdminActions}
            refreshReports={() => refreshReports()}
            showBookingLogsButton={showBookingLogsButton}
            openBookedEventLogsDialog={
              openBookedEventLogsDialog ? () => openBookedEventLogsDialog(item) : undefined
            }
          />
        </td>
      )
    },
  }
}
